import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  GlobalSettings,
  DEFAULT_SETTINGS,
  Voltage,
  Phase,
  ConductorMaterial,
  ConduitType,
  WireSize,
  TemperatureRating,
  UnitSystem,
} from '../engine/types';
import { isNecEdition, setActiveNecEdition, type NecEdition } from '../engine/tables/necEditions';

const STORAGE_KEY = 'bgd-electrical-calc-settings';

interface SettingsContextType {
  settings: GlobalSettings;
  updateSettings: (updates: Partial<GlobalSettings>) => void;
  resetSettings: () => void;
  setVoltage: (voltage: Voltage) => void;
  setPhase: (phase: Phase) => void;
  setMaterial: (material: ConductorMaterial) => void;
  setTemperatureRating: (rating: TemperatureRating) => void;
  setMaxConductorSize: (size: WireSize) => void;
  setConduitType: (type: ConduitType) => void;
  setAmbientTemperature: (temp: number) => void;
  setConduitFillDerating: (enabled: boolean) => void;
  setConduitOversizeEnabled: (enabled: boolean) => void;
  setConduitOversizePercent: (percent: number) => void;
  setUnitSystem: (system: UnitSystem) => void;
  setNecEdition: (edition: NecEdition) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

/**
 * Load settings from localStorage
 */
function loadSettings(): GlobalSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      const merged: GlobalSettings = { ...DEFAULT_SETTINGS, ...parsed };
      if (!isNecEdition(merged.necEdition)) merged.necEdition = DEFAULT_SETTINGS.necEdition;
      return merged;
    }
  } catch (error) {
    console.error('Failed to load settings:', error);
  }
  return DEFAULT_SETTINGS;
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: GlobalSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<GlobalSettings>(loadSettings);

  // Save to localStorage whenever settings change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Non-React code (print/export helpers) reads the edition from the module.
  useEffect(() => {
    setActiveNecEdition(settings.necEdition);
  }, [settings.necEdition]);

  const updateSettings = useCallback((updates: Partial<GlobalSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const setVoltage = useCallback((voltage: Voltage) => {
    updateSettings({ voltage });
  }, [updateSettings]);

  const setPhase = useCallback((phase: Phase) => {
    updateSettings({ phase });
  }, [updateSettings]);

  const setMaterial = useCallback((material: ConductorMaterial) => {
    updateSettings({ conductorMaterial: material });
  }, [updateSettings]);

  const setTemperatureRating = useCallback((rating: TemperatureRating) => {
    updateSettings({ temperatureRating: rating });
  }, [updateSettings]);

  const setMaxConductorSize = useCallback((size: WireSize) => {
    updateSettings({ maxConductorSize: size });
  }, [updateSettings]);

  const setConduitType = useCallback((type: ConduitType) => {
    updateSettings({ conduitType: type });
  }, [updateSettings]);

  const setAmbientTemperature = useCallback((temp: number) => {
    updateSettings({ ambientTemperature: temp });
  }, [updateSettings]);

  const setConduitFillDerating = useCallback((enabled: boolean) => {
    updateSettings({ conduitFillDerating: enabled });
  }, [updateSettings]);

  const setConduitOversizeEnabled = useCallback((enabled: boolean) => {
    updateSettings({ conduitOversizeEnabled: enabled });
  }, [updateSettings]);

  const setConduitOversizePercent = useCallback((percent: number) => {
    updateSettings({ conduitOversizePercent: percent });
  }, [updateSettings]);

  const setUnitSystem = useCallback((system: UnitSystem) => {
    updateSettings({ unitSystem: system });
  }, [updateSettings]);

  const setNecEdition = useCallback((edition: NecEdition) => {
    updateSettings({ necEdition: edition });
  }, [updateSettings]);

  const value: SettingsContextType = {
    settings,
    updateSettings,
    resetSettings,
    setVoltage,
    setPhase,
    setMaterial,
    setTemperatureRating,
    setMaxConductorSize,
    setConduitType,
    setAmbientTemperature,
    setConduitFillDerating,
    setConduitOversizeEnabled,
    setConduitOversizePercent,
    setUnitSystem,
    setNecEdition,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// Hook co-located with its provider; moving it would churn 14 importers for a fast-refresh-only concern.
// eslint-disable-next-line react-refresh/only-export-components
export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export { SettingsContext };
